/**
 * Reports — the viewer (#239). `/reports/:id`.
 *
 * Loads the report (GET /reports/{id}, re-validated server-side against the
 * caller's catalog) and the catalog (for field labels and FK names), then
 * hands the spec to WigReportViewer (wig-react-library /reports), where each widget runs its own query.
 *
 * A widget whose spec no longer validates shows its issues instead of
 * running; a widget withheld from this role shows a neutral placeholder.
 * BD.Admin gets an Edit button into the builder, and Clone: a copy of the
 * report and its SQL sources (POST /reports/{id}/clone) that lands as an
 * unpublished draft in the Preview category, opened in the builder.
 */
import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Drawer,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { ArrowLeftOutlined, CopyOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router";

import { WigAntLayout } from "@warwick-investment-group/wig-react-library";

import SignIn from "../signIn";
import { AppContext } from "../../AppContextProvider";
import usePermissions from "../../hooks/usePermissions";
import useReportsDataSource from "../../hooks/useReportsDataSource";
import reportsService from "../../services/reportsService";
import { apiErrorText } from "../../utils/apiError";
import {
  WigReportAudienceTag,
  WigReportText,
  WigReportViewer,
  WigReportsDataSourceProvider,
  describeIssue,
  validationByWidget,
} from "@warwick-investment-group/wig-react-library/reports";
import { reportLayoutProps } from "./layout";

const { Title, Paragraph } = Typography;

const ReportView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { theme } = useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("reports.view");
  const canManage = can("reports.manage");
  // Who the report is for is land-team context; brokers are not told.
  const seeAudience = can("reports.seeAudience");
  const { dataSource, call } = useReportsDataSource();

  const [report, setReport] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  // The report's `about` summary, opened from beside the title.
  const [aboutOpen, setAboutOpen] = useState(false);
  const [cloning, setCloning] = useState(false);
  const about = report?.spec?.about;

  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const [rep, cat] = await Promise.all([
        call(reportsService.getReport, { id }),
        // The catalog only improves the display (labels, FK names); a report
        // still renders without it.
        call(reportsService.getCatalog).catch(() => null),
      ]);
      setReport(rep?.data?.data || null);
      setCatalog(cat?.data?.data || null);
    } catch (err) {
      setReport(null);
      // The server answers 404 both for a missing report and for one this
      // user may not see (a draft, or a report reading fields hidden from
      // their role) — it never confirms which.
      setErrorMsg(
        err?.response?.status === 404
          ? "This report doesn't exist or you don't have access to it."
          : apiErrorText(err)
      );
    } finally {
      setLoading(false);
    }
  }, [call, id]);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const clone = async () => {
    try {
      setCloning(true);
      const res = await call(reportsService.cloneReport, { id: report.id });
      const copy = res?.data?.data;
      const n = copy?.sources?.length || 0;
      message.success(
        n
          ? `Report cloned with ${n} data source${n === 1 ? "" : "s"}`
          : "Report cloned"
      );
      navigate(`/reports/${copy.id}/edit`);
    } catch (err) {
      message.error(`Couldn't clone the report: ${apiErrorText(err)}`);
    } finally {
      setCloning(false);
    }
  };

  if (!allowed) return null;

  // Issues the server could not pin on a widget (e.g. a spec with no widget
  // list) belong to the report as a whole.
  const reportIssues = validationByWidget(report?.validation)[null] || [];

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row>
          <WigAntLayout
            {...reportLayoutProps(theme === "dark")}
            content={
              <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Space align="center">
                    <Button
                      type="text"
                      aria-label="Back to reports"
                      icon={<ArrowLeftOutlined />}
                      onClick={() => navigate("/reports")}
                    />
                    <Title level={3} style={{ margin: 0 }}>
                      {report?.spec?.title || report?.name || "Report"}
                    </Title>
                    {report?.status === "draft" && <Tag color="gold">Draft</Tag>}
                    {seeAudience && report && <WigReportAudienceTag audience={report.audience} />}
                    {about && (
                      <Button type="link" size="small" onClick={() => setAboutOpen(true)}>
                        About this report
                      </Button>
                    )}
                  </Space>
                  {canManage && report && (
                    <Space>
                      <Button icon={<CopyOutlined />} onClick={clone} loading={cloning}>
                        Clone
                      </Button>
                      <Button
                        icon={<EditOutlined />}
                        onClick={() => navigate(`/reports/${report.id}/edit`)}
                      >
                        Edit
                      </Button>
                    </Space>
                  )}
                </Row>
                {report?.description && (
                  <Paragraph type="secondary" style={{ margin: 0 }}>
                    {report.description}
                  </Paragraph>
                )}
                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    title="Failed to load this report"
                    description={errorMsg}
                  />
                )}
                {reportIssues.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    title="This report has a problem"
                    description={reportIssues.map((i) => describeIssue(i)).join("; ")}
                  />
                )}
                {loading ? (
                  <Skeleton active />
                ) : (
                  report && (
                    <WigReportsDataSourceProvider value={dataSource}>
                      <WigReportViewer
                        spec={report.spec}
                        validation={report.validation}
                        catalog={catalog}
                      />
                    </WigReportsDataSourceProvider>
                  )
                )}
              </Space>
            }
          />
        </Row>
        {/* Rendered like a text widget: markdown, raw HTML off, links in a
            new tab. */}
        <Drawer
          open={aboutOpen && Boolean(about)}
          onClose={() => setAboutOpen(false)}
          title={`About ${report?.spec?.title || report?.name || "this report"}`}
          size={560}
        >
          {about && <WigReportText text={about} />}
        </Drawer>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default ReportView;
